import { isAbortedOrCanceledHttpError, isTransientHttpError } from '@/core/helpers/http-error.helper';
import i18n from '@/i18n';
import * as sentry from '@sentry/vue';
import { AxiosError, AxiosResponse } from 'axios';

interface ErrorData {
   errors?: Record<string, any>;
   errorMessage?: string;
   message?: string;
}

let errorQueue: string[] = [];
let errorTimeout: NodeJS.Timeout | null = null;

/**
 * Auth / sensitive headers redacted before an HTTP error is sent to Sentry.
 * Cookies are intentionally left intact — only credential-bearing headers are scrubbed.
 */
const SENSITIVE_HEADER_KEYS = ['authorization', 'proxy-authorization', 'x-api-key', 'x-auth-token', 'x-csrf-token', 'x-xsrf-token'];

/** Per-body cap so a large request/response payload can't blow past Sentry's event size limit and get silently mangled. */
const MAX_HTTP_BODY_CHARS = 8_000;

function scrubSensitiveHeaders(headers: unknown): unknown {
   if (!headers || typeof headers !== 'object') return headers;

   const result: Record<string, unknown> = {};
   for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
      result[key] = SENSITIVE_HEADER_KEYS.includes(key.toLowerCase()) ? '[REDACTED]' : value;
   }
   return result;
}

async function normalizeBlobErrorResponse(error: AxiosError): Promise<void> {
   const responseData = error.response?.data;
   if (!(responseData instanceof Blob)) return;

   const contentType = String(error.response?.headers?.['content-type'] ?? '');
   if (!contentType.includes('application/json')) return;

   try {
      const text = await responseData.text();
      if (error.response) {
         error.response.data = JSON.parse(text);
      }
   } catch {
      // Keep the original blob when the error body is not valid JSON.
   }
}

function capHttpPayload(value: unknown): unknown {
   if (value === null || value === undefined) return value;

   if (typeof value === 'string') {
      return value.length > MAX_HTTP_BODY_CHARS
         ? `${value.slice(0, MAX_HTTP_BODY_CHARS)}…[truncated ${value.length - MAX_HTTP_BODY_CHARS} chars]`
         : value;
   }

   try {
      const json = JSON.stringify(value);
      if (json === undefined || json.length <= MAX_HTTP_BODY_CHARS) return value;
      return `${json.slice(0, MAX_HTTP_BODY_CHARS)}…[truncated ${json.length - MAX_HTTP_BODY_CHARS} chars]`;
   } catch {
      return '[unserializable]';
   }
}

function getErrorMessage(error: AxiosError): string | null {
   try {
      const errorData = error?.response?.data as ErrorData;

      if (!error?.response) {
         return i18n.global.t('generic.feedback.something-went-wrong');
      }

      if (errorData?.errors) {
         const groupedErrors = Object.values(errorData.errors)
            .map((fieldErrors) => {
               if (Array.isArray(fieldErrors)) {
                  return fieldErrors.map((errorMessage) => String(errorMessage).trim()).filter(Boolean);
               }

               const errorMessage = String(fieldErrors).trim();
               return errorMessage ? [errorMessage] : [];
            })
            .filter((messages) => messages.length > 0);

         const allMessages = groupedErrors.flat();

         if (allMessages.length === 1) {
            return allMessages[0];
         }

         if (allMessages.length > 1) {
            return groupedErrors
               .map((messages) => `<ul>${messages.map((errorMessage) => `<li>${errorMessage}</li>`).join('')}</ul>`)
               .join('<br/>');
         }
      }
      if (errorData?.errorMessage) return errorData.errorMessage;
      if (errorData?.message) return errorData.message;

      if ([400, 422].includes(error.status as number)) {
         return i18n.global.t('generic.feedback.validation');
      }

      if (error.status === 403) {
         return i18n.global.t('generic.feedback.not-allowed');
      }

      if (error.status === 404) {
         return i18n.global.t('generic.feedback.not-found');
      }

      return null;
   } catch {
      return null;
   }
}

function captureSentryError(error: AxiosError): void {
   try {
      const errorName = `[${error.status} ${error.code}] - ${error.config?.url}`;
      sentry.captureException(new Error(errorName), {
         extra: {
            stack: error.stack,
            request: {
               url: error.config?.url,
               method: error.config?.method,
               headers: scrubSensitiveHeaders(error.config?.headers),
               params: capHttpPayload(error.config?.params),
               data: capHttpPayload(error.config?.data),
            },
            response: {
               code: error.code,
               message: error.message,
               status: error.status,
               data: capHttpPayload(error.response?.data),
            },
         },
      });
   } catch {
      console.warn('Error while capturing error');
   }
}

function hasExpectedBusinessError(error: AxiosError<ErrorData>): boolean {
   const errorData = error?.response?.data as ErrorData | undefined;

   return Boolean(errorData?.errors || errorData?.errorMessage || errorData?.message);
}

function shouldCaptureSentryError(error: AxiosError<ErrorData>): boolean {
   const ignoreFromSentry = ['/rejection-reason-types', '/limitation-reason-types'];
   const requestUrl = error.config?.url ?? '';

   if (ignoreFromSentry.some((path) => requestUrl.includes(path))) return false;

   if (isAbortedOrCanceledHttpError(error)) return false;

   // Transient connectivity failures are not actionable application bugs.
   if (isTransientHttpError(error)) return false;

   if (!error.response) return true;

   if (error.status === 500) return true;

   const status = (error.status ?? error.response?.status ?? 0) as number;

   if (status >= 500) return true;

   if (status === 404) return true;

   if ([400, 403, 422].includes(status)) {
      return !hasExpectedBusinessError(error);
   }

   return false;
}

function notifyErrors() {
   if (errorQueue.length > 0) {
      const errorMessage = errorQueue.join('\r\n');
      notifyError({ title: errorMessage || i18n.global.t('generic.feedback.error') });
      errorQueue = [];
   }
}

const errorResponseSuccessInterceptor = function (response: AxiosResponse<any>) {
   return response;
};

const errorResponseErrorInterceptor = async function (error: AxiosError<ErrorData>) {
   if (isAbortedOrCanceledHttpError(error)) return Promise.reject('Request aborted');

   if (isTransientHttpError(error)) return Promise.reject(error);

   await normalizeBlobErrorResponse(error);

   // 404 on DELETE is idempotent — the resource is already gone, which is the desired end state.
   // Skip Sentry capture and user notification to avoid false-positive noise.
   if (error.status === 404 && error.config?.method === 'delete') return Promise.reject(error);

   if (shouldCaptureSentryError(error)) captureSentryError(error);

   let errorMessage = getErrorMessage(error);

   if (!errorMessage) {
      errorMessage = `${i18n.global.t('generic.feedback.error')}\n${(error as any).message ?? ''}`;
   }

   if (!errorQueue.includes(errorMessage)) {
      errorQueue.push(errorMessage);
   }

   if (!errorTimeout) {
      errorTimeout = setTimeout(() => {
         notifyErrors();
         errorTimeout = null;
      }, 300);
   }

   return Promise.reject(error);
};

export { errorResponseErrorInterceptor, errorResponseSuccessInterceptor };
